import moment from 'moment-timezone';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { combineLatest, merge, of, switchMap, timer } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { DatePipe } from '@angular/common';
import {
  CORE_APP_ENV,
  CORE_APP_ID,
  CORE_APP_NAME,
  CORE_APP_VERSION,
  CORE_STARTUP_DATE,
  CORE_STARTUP_ID,
} from '../../../../../core.tokens';
import { DebugFieldComponent, DebugItemsComponent, DebugSizeComponent } from '../../ui';
import {
  CallWaiterService,
  InteractionService,
  Intercom,
  NetworkService,
  UpdateService,
  WebsocketService,
} from '../../../../services';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'core-debug-app',
  templateUrl: './debug-app.component.html',
  styleUrls: ['./debug-app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DebugItemsComponent,
    DebugFieldComponent,
    DebugSizeComponent,
    DatePipe,
  ]
})
export class DebugAppComponent {

  public readonly appStartupId = inject(CORE_STARTUP_ID);
  public readonly appStartupDate = inject(CORE_STARTUP_DATE);
  public readonly appId = inject(CORE_APP_ID);
  public readonly appName = inject(CORE_APP_NAME);
  public readonly appEnv = inject(CORE_APP_ENV);
  public readonly appVersion = inject(CORE_APP_VERSION);
  private readonly update = inject(UpdateService);
  private readonly interaction = inject(InteractionService);
  private readonly network = inject(NetworkService);
  private readonly websocket = inject(WebsocketService);
  private readonly intercom = inject(Intercom);
  private readonly callWaiter = inject(CallWaiterService);


  private readonly date$ = merge(
    of(0),
    timer(0, 5_000),
  ).pipe(
    map(() => moment().format('YYYY-MM-DD HH:mm')),
    distinctUntilChanged(),
  );

  private readonly uptime$ = merge(
    of(0),
    timer(0, 5_000),
  ).pipe(
    map(() => moment.duration(moment().diff(moment(this.appStartupDate))).humanize()),
    distinctUntilChanged(),
  );

  public readonly date = toSignal(this.date$);
  public readonly uptime = toSignal(this.uptime$);

  public readonly callWaiterMainStatus = toSignal(this.callWaiter.mainStatus$);
  public readonly callWaiterMainCount = toSignal(this.callWaiter.mainCount$);
  public readonly callWaiterMainLastAt = toSignal(this.callWaiter.mainLastCallAt$);
  public readonly callWaiterRequestBillStatus = toSignal(this.callWaiter.secondaryStatus$);
  public readonly callWaiterRequestBillCount = toSignal(this.callWaiter.secondaryCount$);
  public readonly callWaiterRequestBillLastAt = toSignal(this.callWaiter.secondaryLastCallAt$);
  public readonly callWaiterAnotherRoundStatus = toSignal(this.callWaiter.tertiaryStatus$);
  public readonly callWaiterAnotherRoundCount = toSignal(this.callWaiter.tertiaryCount$);
  public readonly callWaiterAnotherRoundLastAt = toSignal(this.callWaiter.tertiaryLastCallAt$);

  public readonly updateEnabled = this.update.enabled;
  public readonly updateStatus = toSignal(this.update.status$);
  public readonly updateCheckStrategyTitle = this.update.updateCheckStrategyTitle;
  public readonly updateActivateStrategyTitle = this.update.updateActivateStrategyTitle;

  public readonly interactionStatus = toSignal(this.interaction.idle$);
  public readonly interactionLastInteractionAt = toSignal(this.interaction.lastInteractionAt$);
  public readonly interactionLastStartInteractionAt = toSignal(this.interaction.lastStartInteractionAt$);
  public readonly interactionLastStartIdleAt = toSignal(this.interaction.lastStartIdleAt$);

  public readonly idleDelay = toSignal(this.interaction.idleDelay$, {
    requireSync: true,
  });

  public readonly leftToIdle = toSignal(combineLatest({
    delay: this.interaction.idleDelay$,
    idle: this.interaction.idle$,
  }).pipe(
    switchMap(({ delay, idle }) => {
      if (delay <= 0 || idle) {
        return of(0);
      }

      return timer(0, 500).pipe(
        map(() => delay - moment().diff(moment(this.interaction.lastInteractionAt), 'seconds')),
      );
    }),
    distinctUntilChanged(),
  ));

  public readonly networkStatus = toSignal(this.network.status$);
  public readonly networkConnectionCount = toSignal(this.network.connectionCount$);
  public readonly networkLastConnectionAt = toSignal(this.network.lastConnectionAt$);
  public readonly networkLastDisconnectionAt = toSignal(this.network.lastDisconnectionAt$);

  public readonly websocketStatus = toSignal(this.websocket.status$);
  public readonly websocketConnectionCount = toSignal(this.websocket.connectionCount$);
  public readonly websocketLastConnectionAt = toSignal(this.websocket.lastConnectionAt$);
  public readonly websocketLastDisconnectionAt = toSignal(this.websocket.lastDisconnectionAt$);

  public readonly intercomConnected = toSignal(this.intercom.connected$);
  public readonly intercomConnectionCount = toSignal(this.intercom.connectionCount$);
  public readonly intercomLastConnectionAt = toSignal(this.intercom.lastConnectionAt$);

}
